/**
 * WhatsApp's customer service window: 24 hours after the shopper's last message or the last
 * call they placed (answered or not, C35). Inside it the team and the agent write freely;
 * outside only an approved template reaches them. Meta's free entry point window (a shopper who
 * came from a click-to-WhatsApp ad, `Conversation.freeEntryEndsAt`) makes every message free
 * while it lasts, so it wins while it is open.
 */

const HOUR_MS = 3_600_000;
export const SERVICE_WINDOW_MS = 24 * HOUR_MS;
/** Under this much left the chip warns: a reply now may land after the window shut. */
export const CLOSING_SOON_MS = 2 * HOUR_MS;

export type WindowState =
	| {
			kind: "open";
			endsAt: Date;
			/** Under two hours left. */
			closingSoon: boolean;
			/** What opened it: a message, or a call the shopper placed. */
			by: "message" | "call";
	  }
	| { kind: "free_entry"; endsAt: Date }
	| { kind: "closed"; closedAt: Date | null };

export function windowState({
	lastInboundAt,
	lastInboundCallAt = null,
	fepEndsAt = null,
	now,
}: {
	lastInboundAt: Date | null;
	lastInboundCallAt?: Date | null;
	fepEndsAt?: Date | null;
	now: Date;
}): WindowState {
	if (fepEndsAt && fepEndsAt.getTime() > now.getTime()) {
		return { kind: "free_entry", endsAt: fepEndsAt };
	}
	const byCall =
		lastInboundCallAt !== null &&
		(lastInboundAt === null ||
			lastInboundCallAt.getTime() > lastInboundAt.getTime());
	const last = byCall ? lastInboundCallAt : lastInboundAt;
	if (!last) {
		return { kind: "closed", closedAt: null };
	}
	const endsAt = new Date(last.getTime() + SERVICE_WINDOW_MS);
	const left = endsAt.getTime() - now.getTime();
	if (left <= 0) {
		return { kind: "closed", closedAt: endsAt };
	}
	return {
		kind: "open",
		endsAt,
		closingSoon: left < CLOSING_SOON_MS,
		by: byCall ? "call" : "message",
	};
}

/** "18 h", "1 h 20 min", "12 min", "5 days": how long is left, rounded down. */
export function timeLeft(ms: number): string {
	const minutes = Math.max(0, Math.floor(ms / 60_000));
	if (minutes < 60) {
		return `${Math.max(minutes, 1)} min`;
	}
	const hours = Math.floor(minutes / 60);
	if (hours >= 48) {
		return `${Math.floor(hours / 24)} days`;
	}
	const rest = minutes % 60;
	return hours < 2 && rest ? `${hours} h ${rest} min` : `${hours} h`;
}
